import { Inject } from '@odinjs/odin';
import { ActionAPI, AvatarAPI, BaseModalProps, ButtonAPI, DeckAPI, FetchAPI, FieldAPI, ImageAPI, ModalButton, ModalProps, ModalService, PanelAPI, PDFViewerAPI, TabControlAPI, TabWithContentAPI, TextAPI } from '@philips-internal/oswald-ui-components';
import { ModalButtonType } from '@philips-internal/oswald-ui-constants';
import { Value } from '@philips-internal/oswald-ui-core';
import Controller, { OnAfterFetch, OnClick, OnDynamicContentRender, OnMount } from '@philips-internal/oswald-ui-core/lib/client';
import { ClickEvent, FetchEvent, MountEvent, RenderEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { HttpService, MessageService, } from '@philips-internal/oswald-ui-core/lib/services';
import { generateUUID, isMobile, resolvePath } from '@philips-internal/oswald-ui-utils';
import { default as React } from 'react';
import laboratoryIcon from '../../../../../components/src/assets/images/avatars/avatar_lab.svg';
import radiologyLabIcon from '../../../../../components/src/assets/images/avatars/radiology_lab.svg';
import checkGrayIcon from '../../../../../components/src/assets/images/global/check-blue.svg';
import checkIcon from '../../../../../components/src/assets/images/global/check-green.svg';
import timeIcon from '../../../../../components/src/assets/images/global/hpms-time.svg';
import { FragmentNames, MyChartDischargeSummaryDetailsAliases } from '../../../codegen';
import { YES } from '../../../constants/application-constants';
import { APPLICATION_ENDPOINTS } from '../../../constants/application-endpoints';
import { EXAM_RESULT } from '../../../constants/exam-result';
import { EXAM_STATUS } from '../../../constants/exam-status';
import { EXAM_TYPE } from '../../../constants/exam-type';
import { ExamDetailsItem } from '../../../interfaces/exam-details-item';
import PacsLinkGeneratorService from '../../../services/pacs-link-generator-service';
import ReportService from '../../../services/report-service';
import SummaryContext from '../../../utils/summary-context';
import { DEFAULT_TIMEOUT, showToastNotificationError, showToastNotificationInformation } from '../../../utils/toast-notification-utils';
import UserContext from '../../../utils/user-context';
import BaseController from '../../BaseController';
import { ReportGenerateResponse, SummaryConfig } from '../my-chart-discharge-summary-interfaces';
import { ReportGroup } from '../my-chart-discharge-summary-types';
import './my-chart-discharge-summary-details.css';

@Controller({ fragment: FragmentNames.MY_CHART_DISCHARGE_SUMMARY_DETAILS })
export default class MyChartDischargeSummaryDetailsController extends BaseController {

  @Inject(MyChartDischargeSummaryDetailsAliases.PDF_VIEWER)
    pdfViewerAPI!: PDFViewerAPI;

  @Inject(MyChartDischargeSummaryDetailsAliases.PANEL_LOADER)
    panelLoaderAPI!: PanelAPI;

  @Inject(MyChartDischargeSummaryDetailsAliases.PANEL_EXAM_RESULT)
    panelExamResultAPI!: PanelAPI;

  @Inject(MyChartDischargeSummaryDetailsAliases.PANEL_CHANGING_DOCUMENTS)
    panelChangingDocuments!: PanelAPI;

  @Inject(MyChartDischargeSummaryDetailsAliases.BUTTON_EMAIL)
    emailButtonApi!: ActionAPI;

  @Inject(MyChartDischargeSummaryDetailsAliases.BUTTON_VIEW_UNIFIED_DOCUMENTS)
    viewUnifiedDocumentsApi!: ActionAPI;

  @Inject(MyChartDischargeSummaryDetailsAliases.TAB_WITH_CONTENT_AVAILABLE)
    tabAvailable!: TabWithContentAPI;

  @Inject(MyChartDischargeSummaryDetailsAliases.TAB_WITH_CONTENT_PROCESSING)
    tabProcessing!: TabWithContentAPI;

  @Inject(MyChartDischargeSummaryDetailsAliases.TAB_CONTROL_EXAMS)
    tabControlAPI!: TabControlAPI;

  @Inject(MyChartDischargeSummaryDetailsAliases.FETCH_ENCOUNTER_EXAMS)
    fetchAvailableExams!: FetchAPI

  @Inject(MyChartDischargeSummaryDetailsAliases.FETCH_ENCOUNTER_PROCESSING)
    fetchProcessingExams!: FetchAPI

  @Inject(MyChartDischargeSummaryDetailsAliases.AVATAR)
    avatarAPI!: AvatarAPI;

  @Inject(MyChartDischargeSummaryDetailsAliases.DECK_ENCOUNTER_EXAMS_AVAILABLE)
    deckEncounterExamsAvailableAPI!: DeckAPI;

  @Inject(MyChartDischargeSummaryDetailsAliases.DECK_EXAM_DETAILS_PROCESSING)
    deckExamDetailsProcessingAPI!: DeckAPI;

  pacsLinkGeneratorService: PacsLinkGeneratorService = new PacsLinkGeneratorService();

  abortOnClickCard = false;

  @OnMount({ target: MyChartDischargeSummaryDetailsAliases.FETCH_SUMMARY_DETAILS })
  handleFetchDischargeSummary(event: MountEvent): void {
    const fetchAPI = event.target as FetchAPI;
    this.context.set({ sequence: this.getHistoryRouteParam<string>('summarySequence') });
    this.context.set({ personCode: UserContext.patientId });
    fetchAPI.forceFetch();
  }

  @OnMount({ target: MyChartDischargeSummaryDetailsAliases.LAYOUT_DISCHARGE_SUMMARY_DETAILS })
  handleMountEncounterSummary(): void {
    HttpService.get(APPLICATION_ENDPOINTS.GET_MC_SUMMARY_CONFIG).then(res => {
      const config = res.body as SummaryConfig;
      this.panelChangingDocuments.setVisible(config.showDocumentExchange === YES);
      this.panelExamResultAPI.setVisible(config.showExams === YES);
      this.viewUnifiedDocumentsApi.setEnabled(this.panelExamResultAPI.isVisible() || this.panelChangingDocuments.isVisible());
    })

    this.handleMyChartMedicalDataReportLoading();
  }

  @OnMount({ target: [MyChartDischargeSummaryDetailsAliases.FIELD_AVATAR, MyChartDischargeSummaryDetailsAliases.FIELD_AVATAR_PROCESSING] })
  handleExamTypeIcon(event: MountEvent): void {
    const icons = new Map([
      [EXAM_TYPE.LABORATORY, laboratoryIcon],
      [EXAM_TYPE.PATHOLOGY, laboratoryIcon],
      [EXAM_TYPE.RADIOLOGY, radiologyLabIcon]
    ]);
    const fieldAPI = event.target as FieldAPI;
    const type = fieldAPI.getValue() as EXAM_TYPE;
    const avatarAPI = Array.from(fieldAPI.getChildren()).flat()[1] as AvatarAPI;

    avatarAPI.setImage(icons.get(type));
  }

  @OnMount({ target: MyChartDischargeSummaryDetailsAliases.FIELD_EXAM_STATUS })
  handleExamStatus(event: MountEvent): void {
    const icons = new Map([
      [EXAM_STATUS.AVAILABLE, {expression: 'patient.available', icon: checkIcon}],
      [EXAM_STATUS.PARTIALLY_AVAILABLE, {expression: 'patient.partially-available', icon: timeIcon}],
      [EXAM_STATUS.PROCESSING, {expression: 'patient.processing', icon: timeIcon}],
      [EXAM_STATUS.READY, {expression: 'patient.ready', icon: checkGrayIcon}]
    ]);
    const fieldAPI = event.target as FieldAPI;
    const status = fieldAPI.getValue() as EXAM_STATUS;
    const imageAPI = Array.from(fieldAPI.getChildren()).flat()[1] as ImageAPI;
    const textAPI = Array.from(fieldAPI.getChildren()).flat()[3] as TextAPI;

    imageAPI.setSrc(icons.get(status)?.icon);
    textAPI.setValue(MessageService.translate(icons.get(status)?.expression || 'patient.error'));
  }

  @OnClick({ target: MyChartDischargeSummaryDetailsAliases.DECK_ENCOUNTER_EXAMS_AVAILABLE })
  openExamModal(event: ClickEvent): void {
    if (this.abortOnClickCard) {
      this.abortOnClickCard = false;
      return;
    }
    const examData = event.value as ExamDetailsItem;
    if (examData.blockResult === YES) return;

    const reportService = new ReportService(this);

    const params = {
      CD_ESTABELECIMENTO: examData.cdEstabelecimento,
      CD_MEDICO: examData.cdMedico,
      EXAM_NAME: examData.nmExame,
      EXAM_TYPE: examData.type,
      IE_FORMATO: examData.ieFormato || 0,
      NR_PRESCRICAO: examData.nrPrescricao,
      NR_PRESCRICOES: examData.nrPrescricao,
      NR_SEQ_INTERNOS: examData.internSequenceNumber,
      NR_SEQ_PRESCR: examData.nrSequencia,
      NR_SEQUENCIA_P: examData.nrSeqLaudo,
      NR_SEQUENCIAS: examData.sequenceExam,
      VL_PARAM_STATUS_EXAME: 35
    }

    reportService.gerarRelatorio(params).then(uri => {
      ModalService.openDefault({
        closeable: true,
        contextData: { uri },
        fragment: 'exam-report-modal'
      } as BaseModalProps);
    }).catch(() => showToastNotificationError(MessageService.translate('patient.report-error')));
  }

  @OnMount({ target: MyChartDischargeSummaryDetailsAliases.FIELD_PACS })
  handlePacsButtonVisibility(event: MountEvent): void {
    const fieldAPI = event.target as FieldAPI;
    const dicomCode = String(fieldAPI.getValue() || '');
    const pacsButton = Array.from(fieldAPI.getChildren()).flat()[1] as unknown as ButtonAPI;

    pacsButton.setVisible(dicomCode.length > 0);
    pacsButton.setClassName(dicomCode);
  }

  handleMyChartMedicalDataReportLoading(): void {
    this.panelLoaderAPI.setVisible(false);
    const summarySequence = this.getHistoryRouteParam<number>('summarySequence');
    this.context.set({ summarySequence });
    this.context.set({ personCode: UserContext.patientId });
    const params = {
      groupCode: ReportGroup.DISCHARGE_SUMMARY,
      serviceNumber: SummaryContext.serviceNumber,
      summarySequence: summarySequence,
    }
    const reportDTO = [];
    reportDTO.push(params)
    const body = { reportDTO };

    this.context.set({ reportParams: params });
    this.panelLoaderAPI.setVisible(true);
    const { panelLoaderAPI, showReportOptions } = this;

    HttpService.post<ReportGenerateResponse>(APPLICATION_ENDPOINTS.GET_MC_SUMMARY_DETAIL_DATA_REPORT, { patientId: UserContext.patientId }, { body })
      .then(response => resolvePath(`${APPLICATION_ENDPOINTS.FILE_VIEW_REPORT_CONTENT}?uri=:uri`, { uri: response.body?.uri }))
      .then(file => {
        this.pdfViewerAPI.setSrc(file);
        this.pdfViewerAPI.setVisible(true);
        this.pdfViewerAPI.persistState();
        showReportOptions(true, this.pdfViewerAPI, this.emailButtonApi);
        panelLoaderAPI.setVisible(false);
      })
      .catch(() => {
        showReportOptions(false, this.pdfViewerAPI, this.emailButtonApi);
        panelLoaderAPI.setVisible(false);
      });
  }

  @OnClick({ target: MyChartDischargeSummaryDetailsAliases.BUTTON_VIEW_PACS })
  OnClickPacsBtn(event: ClickEvent): void {
    this.abortOnClickCard = true;
    const { patientId } = UserContext;
    const button = event.target as ButtonAPI;
    const dicomCode = button.getClassName();

    const callPacsInChrome = (): void => {
      const windowReference = window.open();
      this.pacsLinkGeneratorService.getPacsState().then(pacsState => {
        if (!pacsState.enabled) {
          alert(MessageService.translate('patient.no-pacs-available'));
          return;
        }

        this.pacsLinkGeneratorService.openPacsInChrome({
          examId: dicomCode?.toString() || '',
          organizationId: '',
          patientId: patientId.toString(),
        }, windowReference)
      });
    }

    const dialogButtons: ModalButton[] = [
      {
        action: () => undefined,
        title: 'patient.cancel',
        type: ModalButtonType.CANCEL,
      },
      {
        action: callPacsInChrome,
        title: 'patient.continue',
        type: ModalButtonType.SUCCESS,
      }
    ];

    const dialogProps: ModalProps = {
      closeable: false,
      closeByDocument: false,
      closeByEscape: false,
      dialogDescription: 'patient.pacs.open-new-tab',
      dockButtons: dialogButtons,
      focusOnOpen: true,
      title: 'patient.exam-details',
      uuid: generateUUID(),
    };

    ModalService.openDialog(dialogProps);
  }

  @OnClick({ target: [MyChartDischargeSummaryDetailsAliases.BUTTON_EMAIL] })
  onEmailButtonClick(): void {
    const summarySequence = this.getHistoryRouteParam<Value>('summarySequence');
    const mailParams = {
      contents: [
        {
          physicianName: SummaryContext.doctorName,
          serviceNumber: SummaryContext.serviceNumber,
          summarySequence,
        }
      ],
      encounterId: SummaryContext.serviceNumber,
      groupCode: ReportGroup.DISCHARGE_SUMMARY,
      status: this.getAvailableExamStatus(),
      summarySequence,
      unifiedReport: this.viewUnifiedDocumentsApi.isEnabled()
    };

    ModalService.openDefault({
      closeable: true,
      contextData: mailParams,
      dockButtons: [],
      fragment: FragmentNames.SEND_MAIL_SUMMARY,
      title: 'patient.send-by-email',
    });
  }

  @OnClick({ target: [MyChartDischargeSummaryDetailsAliases.BUTTON_VIEW_UNIFIED_DOCUMENTS] })
  onViewUnifiedDocumentsButtonClick(): void {
    if (this.context.get('buttonActive')) {
      showToastNotificationInformation(MessageService.translate('tws.loader.loading'), DEFAULT_TIMEOUT.SHORT);
      return;
    }

    const summarySequence = this.getHistoryRouteParam<number>('summarySequence');
    const reportsParameters = {
      encounterId: SummaryContext.serviceNumber,
      groupCode: ReportGroup.DISCHARGE_SUMMARY,
      patientId: UserContext.patientId,
      status: this.getAvailableExamStatus(),
      summarySequence
    };

    this.context.set({ buttonActive: true });
    HttpService.get<ReportGenerateResponse>(APPLICATION_ENDPOINTS.GET_MC_SUMMARY_DETAIL_UNIFIED_REPORT, reportsParameters)
      .then(response => resolvePath(`${APPLICATION_ENDPOINTS.FILE_VIEW_REPORT_CONTENT}?uri=:uri`, { uri: response.body?.uri }))
      .then(file => {
        const url = file.replace(APPLICATION_ENDPOINTS.FILE_VIEW_REPORT_CONTENT, '');
        this.openModalViewDocument(url);
      })
      .catch(() => showToastNotificationError(MessageService.translate('patient.report-error')))
      .finally(() => this.context.set({ buttonActive: false }));
  }

  private openModalViewDocument(filePath: string): void {
    const className = isMobile() ? '' : 'my-chart-sending-documents__dialog-modal';

    ModalService.openDefault({
      className: className,
      contextData: { filePath },
      fragment: FragmentNames.MY_CHART_VIEW_SENT_DOCUMENT
    });
  }

  private showReportOptions(show: boolean, pdfViewer: PDFViewerAPI, emailButtonApi: ActionAPI): void {
    pdfViewer.setVisible(show);
    emailButtonApi.setEnabled(show);
  }

  @OnMount({target: MyChartDischargeSummaryDetailsAliases.IMAGE_EXAM_STATUS_PROCESSING})
  addIconToProcessingTab(event: MountEvent): void {
    const imageAPI = event.target as ImageAPI;
    imageAPI.setSrc(timeIcon);
  }

  @OnMount({target: MyChartDischargeSummaryDetailsAliases.FETCH_ENCOUNTER_EXAMS})
  handleFetchExamInformation(): void {
    this.fetchAvailableExams.setParameters({
      encounterId: SummaryContext.serviceNumber,
      patientId: UserContext.patientId,
      status: this.getAvailableExamStatus(),
    });
  }

  private getAvailableExamStatus(): string {
    return `${EXAM_STATUS.AVAILABLE},${EXAM_STATUS.READY}`;
  }

  @OnMount({ target: MyChartDischargeSummaryDetailsAliases.FETCH_ENCOUNTER_PROCESSING })
  handleFetchProcessingInformation(): void {
    this.fetchProcessingExams.setParameters({
      encounterId: SummaryContext.serviceNumber,
      patientId: UserContext.patientId,
      status: `${EXAM_STATUS.PROCESSING}`,
    });
  }

  @OnAfterFetch({ target: [MyChartDischargeSummaryDetailsAliases.FETCH_ENCOUNTER_EXAMS, MyChartDischargeSummaryDetailsAliases.FETCH_ENCOUNTER_PROCESSING] })
  afterFetchExamDetails(event: FetchEvent): void {
    const data = event.data as ExamDetailsItem[];

    const availableExams = data.filter(exam => exam.status !== EXAM_STATUS.PROCESSING);
    const processingExams = data.filter(exam => exam.status === EXAM_STATUS.PROCESSING);

    this.tabAvailable.setBadge(availableExams.length);
    this.tabProcessing.setBadge(processingExams.length);
  }

  getDeckAvailableAndReadyTitle(result: string): string {
    switch (result) {
      case EXAM_RESULT.BLOCKED: return MessageService.translate('patient.exam-lab.deck-ready');
      case EXAM_RESULT.AVAILABLE: return MessageService.translate('patient.exam-lab.deck-available');
      default: return '';
    }
  }

  @OnDynamicContentRender({ target: 'deck-encounter-exams-available' })
  handleDeckAvailableAndReadyHeader(event: RenderEvent): void {
    if (event) {
      const { render } = event;
      const data = event.data as ExamDetailsItem[];
      const title = this.getDeckAvailableAndReadyTitle(data[0].blockResult);
      const renderCustomDeckHeader: () => JSX.Element = () => {
        return (
          <div className='c-deck__group-header-default-content' style={{overflow: 'hidden'}}>
            {title}
          </div>
        );
      }
      render(renderCustomDeckHeader);
    }
  }

}
